class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    
    // Add hover functionality for desktop
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.closeTimeout = null;
    this.transitionTimeout = null;
    this.keepOpenTimeout = null;
    this.keepOpenUntil = 0;

    if (this.content) {
      this.content.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.content.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      this.content.addEventListener('pointerdown', this.onMegaFeaturedButtonInteraction.bind(this), true);
      this.content.addEventListener('mousedown', this.onMegaFeaturedButtonInteraction.bind(this), true);
      this.content.addEventListener('touchstart', this.onMegaFeaturedButtonInteraction.bind(this), true);
      this.content.addEventListener('click', this.onMegaFeaturedButtonInteraction.bind(this), true);
    }
    
    // Prevent click on desktop
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    }
  }

  onSummaryClick(event) {
    if (window.innerWidth >= 990) {
      event.preventDefault();
    }
  }

  onMouseEnter() {
    if (window.innerWidth >= 990) {
      clearTimeout(this.closeTimeout);
      clearTimeout(this.transitionTimeout);
      this.mainDetailsToggle.classList.remove('header-menu--closing');
      this.positionDesktopDropdown();
      this.updateHeaderBottomPosition();
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'true');
      this.initMegaMenuFeaturedSwiper();
    }
  }

  onMouseLeave() {
    if (window.innerWidth >= 990) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = setTimeout(() => {
        if (!this.isMenuInteractionActive()) {
          this.close();
        }
      }, 120);
    }
  }

  onMegaFeaturedButtonInteraction(event) {
    const target = event.target;
    const button =
      target instanceof Element ? target.closest('[data-mega-featured-prev], [data-mega-featured-next]') : null;

    if (!button) return;

    this.keepMenuOpen();
  }

  keepMenuOpen() {
    this.keepOpenUntil = Date.now() + 500;
    clearTimeout(this.closeTimeout);
    clearTimeout(this.transitionTimeout);
    clearTimeout(this.keepOpenTimeout);
    this.mainDetailsToggle.classList.remove('header-menu--closing');
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', 'true');

    this.keepOpenTimeout = setTimeout(() => {
      if (!this.isMenuInteractionActive()) this.close();
    }, 520);
  }

  isMenuInteractionActive() {
    const activeElement = document.activeElement;

    return (
      Date.now() < this.keepOpenUntil ||
      this.matches(':hover') ||
      this.content?.matches(':hover') ||
      this.content?.contains(activeElement) ||
      this.isFeaturedButtonActive()
    );
  }

  isFeaturedButtonActive() {
    const activeElement = document.activeElement;
    const featuredButtonSelector = '[data-mega-featured-prev], [data-mega-featured-next]';
    const hoveredFeaturedButton = this.content?.querySelector(
      '[data-mega-featured-prev]:hover, [data-mega-featured-next]:hover'
    );
    const focusedFeaturedButton =
      activeElement instanceof Element ? activeElement.closest(featuredButtonSelector) : null;

    return Boolean(hoveredFeaturedButton || focusedFeaturedButton);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    this.positionDesktopDropdown();
    this.updateHeaderBottomPosition();
    if (this.mainDetailsToggle.open) this.initMegaMenuFeaturedSwiper();
  }

  close(force = false) {
    if (!force && window.innerWidth >= 990 && (Date.now() < this.keepOpenUntil || this.isFeaturedButtonActive())) {
      clearTimeout(this.closeTimeout);
      clearTimeout(this.transitionTimeout);
      this.mainDetailsToggle.classList.remove('header-menu--closing');
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      return;
    }

    if (force) {
      this.keepOpenUntil = 0;
      clearTimeout(this.closeTimeout);
      clearTimeout(this.transitionTimeout);
      clearTimeout(this.keepOpenTimeout);
    }

    if (window.innerWidth >= 990 && this.mainDetailsToggle.hasAttribute('open')) {
      clearTimeout(this.transitionTimeout);
      this.mainDetailsToggle.classList.add('header-menu--closing');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
      this.transitionTimeout = setTimeout(() => {
        this.mainDetailsToggle.classList.remove('header-menu--closing');
        this.mainDetailsToggle.removeAttribute('open');
      }, 180);
      return;
    }

    super.close();
  }

  positionDesktopDropdown() {
    if (window.innerWidth < 990 || !this.content?.classList.contains('header__submenu--desktop-list')) return;

    const summary = this.mainDetailsToggle.querySelector('summary');
    const summaryRect = summary?.getBoundingClientRect();
    const headerRect = this.header?.getBoundingClientRect();
    if (!summaryRect || !headerRect) return;

    const dropdownWidth = 280;
    const left = Math.min(Math.max(summaryRect.left, 16), window.innerWidth - dropdownWidth - 16);
    this.content.style.left = `${Math.round(left)}px`;
    this.content.style.top = `${Math.floor(headerRect.bottom)}px`;
  }

  updateHeaderBottomPosition() {
    if (!this.header) return;

    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  initMegaMenuFeaturedSwiper() {
    if (!this.content?.classList.contains('mega-menu__content')) return;

    window.requestAnimationFrame(() => {
      const swiperEl = this.content.querySelector('[data-mega-featured-swiper]');
      if (!swiperEl) return;

      if (typeof Swiper === 'undefined') {
        if (typeof window.manasLoadSwiper === 'function') {
          window.manasLoadSwiper().then(() => this.initMegaMenuFeaturedSwiper());
        }
        return;
      }

      if (swiperEl.swiper) {
        swiperEl.swiper.update();
        return;
      }

      new Swiper(swiperEl, {
        slidesPerView: 2,
        spaceBetween: 20,
        watchOverflow: true,
        navigation: {
          prevEl: this.content.querySelector('[data-mega-featured-prev]'),
          nextEl: this.content.querySelector('[data-mega-featured-next]'),
        },
      });
    });
  }
}

customElements.define('header-menu', HeaderMenu);
